import { useMemo } from 'react';
import { useStation } from './queries/stationQueries';
import { usePowerMeterDemand, usePowerMeterDemandYesterday } from './queries/powerMeterQueries';
import { useCbl, usePredictionCbl } from './queries/cblQueries';
import dayjs from 'dayjs';
import { useDRResource } from './queries/drResourceQueries';
import { useDrHistoryLatest } from './queries/drHistoryQueries';
import { useSetUpList } from './queries/setUpQueries';
export const useStationDetail = (id) => {
  const today = dayjs().format('YYYY-MM-DD');
  const { data: station, isLoading, error } = useStation(id);
  const powerMeterId = station?.PowerMeterContractNumber;
  const drResourceId = station?.DrResourceSysId;

  const { data: setUpData } = useSetUpList();
  const defaultCblModuleName = setUpData?.Items?.find(item => item.Group === 'DefaultCBL' && item.Key === 'DEFAULT_MODULE_NAME')?.Value;
  const defaultCblModuleVersion = setUpData?.Items?.find(item => item.Group === 'DefaultCBL' && item.Key === 'DEFAULT_MODULE_VERSION')?.Value;

  const {
    data: drResourceData,
    isLoading: drResourceLoading,
    error: drResourceError,
    refetch: refetchDrResource
  } = useDRResource(drResourceId);

  const { 
    data: powerMeterDemand, 
    isLoading: powerMeterDemandLoading, 
    error: powerMeterDemandError,
    refetch: refetchPowerMeterDemand
  } = usePowerMeterDemand(powerMeterId);

  const {
    data: powerMeterDemandYesterday,
    isLoading: powerMeterDemandYesterdayLoading,
    error: powerMeterDemandYesterdayError,
    refetch: refetchPowerMeterDemandYesterday
  } = usePowerMeterDemandYesterday({
    targetDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
    StationSysIdList: [id]
  });
  

  const {
    data: cblData,
    isLoading: cblLoading,
    error: cblError,
    refetch: refetchCbl
  } = useCbl(id, 
    'AIsample',
    {
      alModelName: 'AIsample',
      alModelVersion: "1.0",
      targetDate: dayjs().format('YYYY-MM-DD'),
      stationSysIdList: [id]
    }
  );
  
  // drResourceData를 가져온 후에 cblModuleName과 cblModuleVersion 계산
  const { cblModuleName, cblModuleVersion } = useMemo(() => {
    if (!drResourceData) {
      return { cblModuleName: defaultCblModuleName, cblModuleVersion: defaultCblModuleVersion };
    }
    return {
      cblModuleName: drResourceData?.AppliiedCblAlgorithm && drResourceData?.AppliiedCblAlgorithm !== '' 
        ? drResourceData?.AppliiedCblAlgorithm 
        : defaultCblModuleName,
      cblModuleVersion: drResourceData?.AppliedCblAlgorithmVersion && drResourceData?.AppliedCblAlgorithmVersion !== '' 
        ? drResourceData?.AppliedCblAlgorithmVersion 
        : defaultCblModuleVersion
    };
  }, [drResourceData, defaultCblModuleName, defaultCblModuleVersion]);

  const {
    data: predictionCblData,
    isLoading: predictionCblLoading,
    error: predictionCblError,
    refetch: refetchPredictionCbl
  } = usePredictionCbl({ 
    targetDate: dayjs().format('YYYY-MM-DD'), 
    stationSysIdList: [id],
    cblModuleName,
    cblModuleVersion,
    enabled: !!drResourceData || !!defaultCblModuleName // drResourceData 또는 defaultCblModuleName이 있을 때만 실행
  });

  const {
    data: drHistoryLatestData,
    isLoading: drHistoryLatestLoading,
    error: drHistoryLatestError,
    refetch: refetchDrHistoryLatest
  } = useDrHistoryLatest();

  const {
    data: powerMeterDemandDrEvent,
    isLoading: powerMeterDemandDrEventLoading,
    error: powerMeterDemandDrEventError,
    refetch: refetchPowerMeterDemandDrEvent
  } = usePowerMeterDemandYesterday({
    targetDate: dayjs(drHistoryLatestData?.date).format('YYYY-MM-DD'),
    StationSysIdList: [id]
  });


  const getPowerMeterDemand = async () => {
    if (powerMeterId) {
      await refetchPowerMeterDemand();
    }
  };
    
  return { 
    station, 
    powerMeterDemand,
    powerMeterDemandDrEvent,
    powerMeterDemandDrEventLoading,
    powerMeterDemandDrEventError,
    refetchPowerMeterDemandDrEvent,
    drResourceData,
    drResourceLoading,
    drResourceError,
    refetchDrResource,
    isLoading, 
    error, 
    powerMeterDemandLoading, 
    powerMeterDemandError, 
    powerMeterDemandYesterday,
    powerMeterDemandYesterdayLoading,
    powerMeterDemandYesterdayError,
    refetchPowerMeterDemandYesterday,
    getPowerMeterDemand,
    cblData,
    cblLoading,
    cblError,
    refetchCbl,
    predictionCblData,
    predictionCblLoading,
    predictionCblError,
    refetchPredictionCbl,
    drHistoryLatestData,
    drHistoryLatestLoading,
    drHistoryLatestError,
    refetchDrHistoryLatest
  };
};

export default useStationDetail;